import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ListChecks, Sparkle, Plus, Trash, CheckCircle, XCircle, ArrowClockwise, ChartBar, Lock, Clock } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import CopyGuard from "@/components/CopyGuard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const LETTERS = ["A", "B", "C", "D", "E"];

/** Contrôle de compréhension affiché à l'étudiant, en fin de séquence. */
export function LessonCheck({ lesson, onPassed }) {
  const info = lesson.check_info;
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [leaves, setLeaves] = useState(0);
  const [closed, setClosed] = useState(false); // fermé jusqu'à demain (état local, le serveur fait foi)
  const lastLeave = useRef(0);
  const sending = useRef(false);

  // changer de séquence remet le contrôle à zéro
  useEffect(() => { setQuestions(null); setAnswers([]); setResult(null); setTimeLeft(null); setLeaves(0); setClosed(false); sending.current = false; }, [lesson.id]);

  // Pendant le contrôle : on compte les sorties de page (autre onglet, autre application)
  const running = !!questions && !result;
  useEffect(() => {
    if (!running) return undefined;
    const onLeave = () => {
      const now = Date.now();
      if (now - lastLeave.current < 1500) return;
      lastLeave.current = now;
      setLeaves((n) => n + 1);
      api.post(`/lessons/${lesson.id}/check/tab-event`).catch(() => {});
    };
    const onVis = () => { if (document.hidden) onLeave(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onLeave);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", onLeave); };
  }, [running, lesson.id]);

  // Chrono : à zéro, envoi automatique des réponses données
  useEffect(() => {
    if (timeLeft === null || result) return undefined;
    if (timeLeft <= 0) { if (!sending.current) { toast.error("Temps écoulé, envoi automatique de vos réponses."); submit(); } return undefined; }
    const t = setTimeout(() => setTimeLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, result]);

  if (!info) return null;
  const lockedUntilTomorrow = closed || !!info.locked_until;

  const start = async () => {
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/check/start`);
      sending.current = false; setLeaves(0);
      setQuestions(data.questions); setAnswers(data.questions.map(() => null)); setResult(null);
      setTimeLeft(data.time_limit_minutes ? (data.time_left_seconds ?? data.time_limit_minutes * 60) : null);
    } catch (e) { toast.error(apiError(e)); if (e?.response?.status === 403) setClosed(true); }
  };
  const submit = async () => {
    if (sending.current) return;
    sending.current = true; setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/check/attempt`, { answers });
      setResult(data); setTimeLeft(null);
      if (data.timed_out && !data.passed) { setClosed(true); toast.error("Temps écoulé : ce contrôle est fermé jusqu'à demain."); }
      if (data.passed) onPassed && onPassed();
    } catch (e) { toast.error(apiError(e)); setQuestions(null); setTimeLeft(null); setClosed(e?.response?.status === 403); } finally { setBusy(false); }
  };
  const answered = answers.length > 0 && answers.every((a) => a !== null);

  return (
    <CopyGuard active noSelect className="mt-8 rounded-xl border border-border bg-card p-5" data-testid="lesson-check">
      <div className="flex flex-wrap items-center gap-2">
        <ListChecks size={22} weight="fill" className="text-primary" />
        <h3 className="font-display text-lg font-bold">Contrôle de compréhension</h3>
        {info.required
          ? <Badge className="bg-accent text-accent-foreground"><Lock size={12} className="mr-1" /> Obligatoire pour continuer</Badge>
          : <Badge variant="secondary">Facultatif</Badge>}
        {info.passed && <Badge className="bg-emerald-600 text-white"><CheckCircle size={12} weight="fill" className="mr-1" /> Réussi</Badge>}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {info.count} question{info.count > 1 ? "s" : ""} · seuil de réussite {info.pass_pct} %
        {info.time_limit_minutes && ` · ${info.time_limit_minutes} min chrono`}
        {info.best_pct !== null && ` · votre meilleur score : ${info.best_pct} %`}
      </p>

      {!questions && (
        <div className="mt-4">
          {info.required && !info.passed && <p className="mb-3 text-sm">Réussissez ce contrôle pour débloquer la séquence suivante. Vous pouvez le refaire autant de fois que nécessaire.</p>}
          {info.time_limit_minutes && !lockedUntilTomorrow && <p className="mb-3 text-sm text-muted-foreground">Chronométré : {info.time_limit_minutes} min dès l'ouverture. Passé ce délai, vos réponses sont envoyées automatiquement et, si le seuil n'est pas atteint, le contrôle se ferme jusqu'à demain.</p>}
          {lockedUntilTomorrow
            ? <Button disabled variant="outline" data-testid="check-start"><Clock size={16} className="mr-2" /> Fermé jusqu'à demain</Button>
            : (
              <Button onClick={start} className="bg-primary hover:bg-primary/90" data-testid="check-start">
                {info.attempts > 0 ? <><ArrowClockwise size={16} className="mr-2" /> Refaire le contrôle</> : "Commencer le contrôle"}
              </Button>
            )}
        </div>
      )}

      {questions && (
        <div className="mt-4 space-y-5">
          {timeLeft !== null && !result && (
            <div className="flex flex-wrap items-center gap-3">
              <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-sm font-mono ${timeLeft <= 30 ? "bg-accent/10 text-accent" : "bg-secondary text-foreground"}`} data-testid="check-timer">
                <Clock size={16} /> {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:{String(timeLeft % 60).padStart(2, "0")}
              </span>
              <span className="text-xs text-muted-foreground">Les réponses données sont envoyées automatiquement à la fin du temps.</span>
            </div>
          )}
          {leaves > 0 && !result && (
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="check-leaves">
              Vous avez quitté la page {leaves} fois pendant ce contrôle. Votre professeur en est informé.
            </p>
          )}
          {questions.map((q, qi) => {
            const r = result?.results?.[qi];
            return (
              <div key={q.id} className="space-y-2" data-testid={`check-q-${qi}`}>
                <p className="text-sm font-semibold">{qi + 1}. {q.text}</p>
                <div className="grid gap-2">
                  {q.options.map((o, oi) => {
                    const chosen = answers[qi] === oi;
                    let cls = chosen ? "border-primary bg-primary/10" : "border-border hover:border-primary/60";
                    if (r) {
                      if (oi === r.correct_index) cls = "border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30";
                      else if (chosen) cls = "border-destructive bg-destructive/10";
                      else cls = "border-border opacity-70";
                    }
                    return (
                      <button key={oi} type="button" disabled={!!result}
                        onClick={() => setAnswers((a) => a.map((x, i) => (i === qi ? oi : x)))}
                        className={`flex items-start gap-3 rounded-lg border p-3 text-left text-sm transition-colors ${cls}`}
                        data-testid={`check-opt-${qi}-${oi}`}>
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-current text-xs font-bold">{LETTERS[oi]}</span>
                        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{o}</span>
                        {r && oi === r.correct_index && <CheckCircle size={18} weight="fill" className="shrink-0 text-emerald-600" />}
                        {r && chosen && oi !== r.correct_index && <XCircle size={18} weight="fill" className="shrink-0 text-destructive" />}
                      </button>
                    );
                  })}
                </div>
                {r && r.explanation && <p className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">{r.explanation}</p>}
              </div>
            );
          })}

          {!result && (
            <Button onClick={submit} disabled={(!answered && timeLeft === null) || busy} data-testid="check-submit">{busy ? "Correction…" : "Valider mes réponses"}</Button>
          )}
          {result && (
            <div className={`rounded-lg p-4 ${result.passed ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-amber-50 dark:bg-amber-950/30"}`} data-testid="check-result">
              <p className="font-display text-lg font-bold">{result.correct} / {result.total} · {result.pct} %</p>
              <p className="text-sm">
                {result.passed
                  ? (result.required
                    ? (lesson.completed ? "Bravo, contrôle réussi : la séquence suivante est débloquée." : "Bravo, contrôle réussi. Dernière étape : cliquez sur « Marquer comme terminé » en bas de la séquence pour débloquer la suivante.")
                    : "Bravo, contrôle réussi.")
                  : `Il faut ${result.pass_pct} % pour réussir. Relisez la séquence, puis réessayez.`}
              </p>
              {result.timed_out && <p className="mt-1 text-sm font-semibold">Temps écoulé.{result.locked_until ? " Ce contrôle est fermé jusqu'à demain." : ""}</p>}
              {!result.locked_until && <Button className="mt-3" variant="outline" onClick={start} data-testid="check-retry"><ArrowClockwise size={16} className="mr-2" /> {result.passed ? "Refaire" : "Réessayer"}</Button>}
            </div>
          )}
        </div>
      )}
    </CopyGuard>
  );
}

const blankQuestion = () => ({ id: null, text: "", options: ["", "", "", ""], correct: 0, explanation: "" });

/** Éditeur du contrôle de compréhension, pour le professeur. */
export function CheckEditor({ lesson, onClose, onSaved }) {
  const [tab, setTab] = useState("questions");
  const [qs, setQs] = useState(null);
  const [required, setRequired] = useState(false);
  const [passPct, setPassPct] = useState(60);
  const [timeLimit, setTimeLimit] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [count, setCount] = useState(5);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get(`/lessons/${lesson.id}/check`).then((r) => {
      setQs(r.data.questions); setRequired(!!r.data.required); setPassPct(r.data.pass_pct ?? 60);
      setTimeLimit(r.data.time_limit_minutes ? String(r.data.time_limit_minutes) : "");
    }).catch((e) => toast.error(apiError(e)));
  }, [lesson.id]);
  useEffect(() => {
    if (tab === "results" && !stats) api.get(`/lessons/${lesson.id}/check/stats`).then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e)));
  }, [tab]);

  const upd = (i, patch) => setQs((a) => a.map((q, k) => (k === i ? { ...q, ...patch } : q)));
  const setOpt = (i, oi, v) => upd(i, { options: qs[i].options.map((o, k) => (k === oi ? v : o)) });
  const addOpt = (i) => qs[i].options.length < 5 && upd(i, { options: [...qs[i].options, ""] });
  const delOpt = (i, oi) => {
    const q = qs[i];
    if (q.options.length <= 2) return;
    const options = q.options.filter((_, k) => k !== oi);
    upd(i, { options, correct: q.correct === oi ? 0 : q.correct > oi ? q.correct - 1 : q.correct });
  };

  const suggest = async () => {
    if (qs.some((q) => q.text.trim()) && !window.confirm("Les questions proposées seront ajoutées à la suite des vôtres. Continuer ?")) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/check/suggest`, { count: Number(count) });
      setQs((a) => [...a.filter((q) => q.text.trim()), ...data.questions]);
      toast.success("Questions proposées : relisez-les et corrigez-les avant d'enregistrer.");
    } catch (e) { toast.error(apiError(e)); } finally { setSuggesting(false); }
  };

  const save = async () => {
    const clean = qs.filter((q) => q.text.trim() || q.options.some((o) => o.trim()));
    for (const [i, q] of clean.entries()) {
      if (q.text.trim().length < 3) return toast.error(`Question ${i + 1} : écrivez l'énoncé.`);
      if (q.options.some((o) => !o.trim())) return toast.error(`Question ${i + 1} : remplissez toutes les réponses (ou supprimez celles en trop).`);
    }
    setBusy(true);
    try {
      await api.put(`/lessons/${lesson.id}/check`, { questions: clean, required, pass_pct: Number(passPct), time_limit_minutes: timeLimit ? Number(timeLimit) : null });
      toast.success(clean.length ? "Contrôle enregistré" : "Contrôle supprimé");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="check-editor">
        <DialogHeader>
          <DialogTitle>Contrôle de compréhension</DialogTitle>
          <DialogDescription>{lesson.title}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button size="sm" variant={tab === "questions" ? "default" : "outline"} onClick={() => setTab("questions")}><ListChecks size={14} className="mr-1" /> Questions</Button>
          <Button size="sm" variant={tab === "results" ? "default" : "outline"} onClick={() => setTab("results")} data-testid="check-tab-results"><ChartBar size={14} className="mr-1" /> Résultats</Button>
        </div>

        {tab === "questions" && qs && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4" checked={required} onChange={(e) => setRequired(e.target.checked)} data-testid="check-required" />
                <span><b>Obligatoire</b> : l'étudiant doit le réussir pour passer à la séquence suivante (si l'ordre est imposé dans le cours)</span>
              </label>
              <div className="flex items-center gap-2">
                <Label className="whitespace-nowrap">Seuil de réussite (%)</Label>
                <Input type="number" min={0} max={100} value={passPct} onChange={(e) => setPassPct(e.target.value)} className="w-20" data-testid="check-pass" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="whitespace-nowrap">Temps limite (min, optionnel)</Label>
                <Input type="number" min={1} max={240} value={timeLimit} onChange={(e) => setTimeLimit(e.target.value)} placeholder="Sans limite" className="w-28" data-testid="check-time-limit" />
              </div>
            </div>

            {qs.length === 0 && <p className="text-sm text-muted-foreground">Aucune question. Ajoutez-en une, ou demandez des propositions à l'IA à partir du texte de la séquence.</p>}
            {qs.map((q, i) => (
              <div key={q.id || i} className="space-y-2 rounded-lg border border-border p-3" data-testid={`edit-q-${i}`}>
                <div className="flex items-start gap-2">
                  <span className="mt-2 text-sm font-bold text-muted-foreground">{i + 1}.</span>
                  <Textarea rows={2} value={q.text} onChange={(e) => upd(i, { text: e.target.value })} placeholder="Énoncé de la question" data-testid={`edit-q-text-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => setQs((a) => a.filter((_, k) => k !== i))} title="Supprimer la question"><Trash size={16} className="text-accent" /></Button>
                </div>
                <p className="text-xs text-muted-foreground">Cochez le rond de la bonne réponse.</p>
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input type="radio" name={`correct-${i}`} className="h-4 w-4" checked={q.correct === oi} onChange={() => upd(i, { correct: oi })} data-testid={`edit-correct-${i}-${oi}`} />
                    <span className="w-5 text-sm font-bold text-muted-foreground">{LETTERS[oi]}</span>
                    <Input value={o} onChange={(e) => setOpt(i, oi, e.target.value)} placeholder={`Réponse ${LETTERS[oi]}`} data-testid={`edit-opt-${i}-${oi}`} />
                    <Button size="icon" variant="ghost" disabled={q.options.length <= 2} onClick={() => delOpt(i, oi)} title="Retirer cette réponse"><Trash size={14} /></Button>
                  </div>
                ))}
                {q.options.length < 5 && <Button size="sm" variant="ghost" onClick={() => addOpt(i)}><Plus size={14} className="mr-1" /> Ajouter une réponse</Button>}
                <Input value={q.explanation} onChange={(e) => upd(i, { explanation: e.target.value })} placeholder="Explication affichée après la correction (facultatif)" />
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setQs((a) => [...a, blankQuestion()])} data-testid="check-add-q"><Plus size={16} className="mr-1" /> Ajouter une question</Button>
              <div className="flex items-center gap-1">
                <Button variant="outline" onClick={suggest} disabled={suggesting} data-testid="check-suggest"><Sparkle size={16} weight="fill" className="mr-1 text-accent" /> {suggesting ? "L'IA réfléchit…" : "Proposer des questions (IA)"}</Button>
                <select value={count} onChange={(e) => setCount(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" aria-label="Nombre de questions">
                  {[3, 4, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {tab === "results" && (
          <div className="space-y-4" data-testid="check-results">
            {!stats && <p className="text-sm text-muted-foreground">Chargement…</p>}
            {stats && stats.students === 0 && <p className="text-sm text-muted-foreground">Aucun étudiant n'a encore passé ce contrôle.</p>}
            {stats && stats.students > 0 && (
              <>
                <p className="text-sm">{stats.students} étudiant(s) sur {stats.students_total} ont passé le contrôle · moyenne des meilleurs scores : <b>{stats.avg_best_pct} %</b> · taux de réussite : <b>{stats.pass_rate} %</b></p>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Questions les moins réussies en premier</p>
                  {[...stats.questions].sort((a, b) => (a.correct_rate ?? 101) - (b.correct_rate ?? 101)).map((q) => (
                    <div key={q.id} className="mb-2">
                      <div className="flex justify-between gap-3 text-sm"><span className="min-w-0 truncate">{q.text}</span><b>{q.correct_rate ?? "—"} %</b></div>
                      <div className="h-2 rounded bg-muted"><div className="h-2 rounded bg-primary" style={{ width: `${q.correct_rate || 0}%` }} /></div>
                    </div>
                  ))}
                </div>
                <div className="max-h-52 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground"><tr><th className="p-2">Étudiant</th><th className="p-2">Meilleur score</th><th className="p-2">Essais</th><th className="p-2">Résultat</th><th className="p-2" title="Sorties de la page pendant le contrôle">Sorties</th><th className="p-2">Temps écoulé</th></tr></thead>
                    <tbody>
                      {stats.rows.map((r) => (
                        <tr key={r.student_id} className="border-t border-border">
                          <td className="p-2">{r.name}</td><td className="p-2">{r.best_pct} %</td><td className="p-2">{r.attempts}</td>
                          <td className="p-2">{r.passed ? <span className="text-emerald-600">Réussi</span> : <span className="text-destructive">À revoir</span>}</td>
                          <td className="p-2">{r.tab_switches || 0}</td><td className="p-2">{r.timed_out ? `${r.timed_out} fois` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {tab === "questions" && <Button onClick={save} disabled={busy || !qs} data-testid="check-save">{busy ? "Enregistrement…" : "Enregistrer le contrôle"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
